<template>
  <div>
    <div :class="['space-y-4', { 'overflow-y-auto max-h-[calc(100vh-25rem)] p-2': !isMobile }]">
      <template v-if="isMobile">
        <TCollapse class="mb-6">
          <template #trigger>
            <div class="flex items-center gap-2">
              <TIcon
                :icon="['fal', 'rv']"
                size="lg"
                fixed-width
              />
              <span class="font-semibold">
                {{ t('searchFilters.rvType.drivable.title') }}
                <template v-if="drivableRvCount">
                  ({{ drivableRvCount }})
                </template>
              </span>
            </div>
          </template>

          <SearchFilterRvType
            type="drivable"
            :model-value="rvTypesModel"
            class="mt-2"
            @update:model-value="handleRvTypesChange"
          />
        </TCollapse>

        <TCollapse class="mb-6">
          <template #trigger>
            <div class="flex items-center gap-2">
              <TIcon
                :icon="['fal', 'caravan']"
                size="lg"
                fixed-width
              />
              <span class="font-semibold">
                {{ t('searchFilters.rvType.towable.title') }}
                <template v-if="towableRvCount">
                  ({{ towableRvCount }})
                </template>
              </span>
            </div>
          </template>

          <SearchFilterRvType
            type="towable"
            :model-value="rvTypesModel"
            class="mt-2"
            @update:model-value="handleRvTypesChange"
          />
        </TCollapse>

        <hr>

        <div class="mb-4">
          <div class="flex justify-between items-center">
            <span class="font-semibold">
              {{ t('searchFilters.delivery.title') }}
            </span>

            <TToggle
              v-model="deliveryModel"
              size="xs"
            />
          </div>
          <span class="text-sm">{{ t('searchFilters.delivery.description') }}</span>
        </div>

        <div class="mb-4">
          <div class="flex justify-between items-center">
            <span class="font-semibold">{{ t('searchFilters.instantBook.title') }}<TIcon
              :icon="['fas', 'bolt']"
              size="sm"
              class="ml-1"
            /></span>

            <TToggle
              v-model="instabookModel"
             
              size="xs"
            />
          </div>

          <span class="text-sm">{{ t('searchFilters.instantBook.description') }}</span>
        </div>

        <div class="mb-4">
          <div class="flex justify-between items-center">
            <span class="font-semibold">{{ t('searchFilters.moreFilters.airportPickup.title') }}</span>

            <TToggle
              v-model="airportPickupModel"
              size="xs"
            />
          </div>

          <span class="text-sm">{{ t('searchFilters.moreFilters.airportPickup.description') }}</span>
        </div>

        <hr>
      </template>

      <p>
        <span class="font-semibold">{{ t('searchFilters.price.priceRange') }}</span>
      </p>

      <SearchFilterSlider
        :range-object="priceModel"
        :min="0"
        :max="500"
        :label-formatter="'${value}'"
        plus-on-max-value
        class="mb-4"
        @update="handlePriceChange"
      />

      <div class="mb-4 grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2">
        <div>
          <p>
            <span class="font-semibold">{{ t('searchFilters.weight') }}</span>
          </p>

          <SearchFilterSlider
            :range-object="weightModel"
            :min="0"
            :max="RV_WEIGHT_MAX"
            :step="1000"
            :label-formatter="'{value} lbs'"
            plus-on-max-value
            @update="handleWeightChange"
          />
        </div>

        <div>
          <p>
            <span class="font-semibold">{{ t('searchFilters.length') }}</span>
          </p>

          <SearchFilterSlider
            :range-object="lengthModel"
            :min="0"
            :max="RV_LENGTH_MAX"
            :step="5"
            :label-formatter="'{value}’'"
            plus-on-max-value
            @update="handleLengthChange"
          />
        </div>

        <div>
          <p class="mb-1">
            <span class="font-semibold">{{ t('rvManufacturer') }}</span>
          </p>

          <SearchFilterRvBrand v-model="rvBrandModel" />
        </div>

        <div>
          <p>
            <span class="font-semibold">{{ t('rvYear') }}</span>
          </p>

          <SearchFilterSlider
            :range-object="yearModel"
            :min="yearMin"
            :max="yearMax"
            :step="1"
            @update="handleYearChange"
          />
        </div>
      </div>

      <hr>

      <div>
        <p class="mb-2">
          <span class="font-semibold">{{ t('amenities') }}</span>
        </p>

        <SearchFilterAmenities
          v-model="amenitiesModel"
          :show-categories="!isMobile"
        >
          <template #Other-start>
            <TCheckbox
              v-model="superhostModel"
              class="my-1"
              :label="t('searchFilters.moreFilters.superhost')"
            />
          </template>

          <template #Other-end>
            <TCheckbox
              v-model="petFriendlyModel"
              class="my-1"
              :label="t('searchFilters.petFriendly')"
            />

            <TCheckbox
              v-if="!isMobile"
              v-model="airportPickupModel"
              class="my-1"
              :label="t('searchFilters.moreFilters.airportPickup.title')"
            />
          </template>
        </SearchFilterAmenities>
      </div>

      <hr>

      <div class="mt-4">
        <p class="mb-2">
          <span class="font-semibold">{{ t('rules') }}</span>
        </p>

        <div class="mb-4 grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2">
          <TCheckbox
            id="checkbox-festival"
            v-model="festivalFriendlyModel"
            :label="t('festivalEventsFriendly')"
          />

          <TCheckbox
            id="checkbox-unlimited-mileage"
            v-model="unlimitedMileageModel"
            :label="t('unlimitedMileage')"
          />

          <TCheckbox
            id="checkbox-inexperienced"
            v-model="experienceModel"
            :label="t('experienceNotRequired')"
          />
        </div>
      </div>
    </div>

    <div class="w-full flex justify-between gap-4">
      <TButton
        size="sm"
        variant="secondary"
        @click="clear"
      >
        {{ t('actions.clear') }}
      </TButton>

      <TButton
        size="sm"
       
        @click="apply"
      >
        {{ t('actions.apply') }}
      </TButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type RV_TYPES, MOTORIZED_RV_TYPES, RV_LENGTH_MAX, RV_MAX_DEFAULT_AGE, RV_WEIGHT_MAX, TOWABLE_RV_TYPES } from '~/constants/rv'

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { isMobile } = useDevice()
const { applyMenuFilter, searchData, clearRvSearchFilters, navigateToRvSearch } = useSearch()

// Arrays are copied so editing the drafts never mutates the shared search state before applying.
const rvTypesModel = ref([...(searchData.value.types ?? [])])
const drivableRvCount = computed(() =>
  MOTORIZED_RV_TYPES.filter((type) => rvTypesModel.value.includes(type)).length,
)
const towableRvCount = computed(() =>
  TOWABLE_RV_TYPES.filter((type) => rvTypesModel.value.includes(type)).length,
)
const deliveryModel = ref(searchData.value.delivery)
const instabookModel = ref(searchData.value.instantBook)
const amenitiesModel = ref([...(searchData.value.amenities ?? [])])

const petFriendlyModel = ref(searchData.value.guests?.petFriendly)
const airportPickupModel = ref(searchData.value.airportPickup)

const priceModel = ref({
  min: searchData.value.minPrice,
  max: searchData.value.maxPrice,
})

function handlePriceChange({ min, max }: { min?: number, max?: number }) {
  priceModel.value = { min, max }
}

const weightModel = ref({
  min: searchData.value.minWeight,
  max: searchData.value.maxWeight,
})

function handleWeightChange({ min, max }: { min?: number, max?: number }) {
  weightModel.value = { min, max }
}

const lengthModel = ref({
  min: searchData.value.minLength,
  max: searchData.value.maxLength,
})

function handleLengthChange({ min, max }: { min?: number, max?: number }) {
  lengthModel.value = { min, max }
}

const yearModel = ref({
  min: searchData.value.minYear,
  max: searchData.value.maxYear,
})

function handleYearChange({ min, max }: { min?: number, max?: number }) {
  yearModel.value = { min, max }
}

const yearMin = computed(() => datetime().year() - RV_MAX_DEFAULT_AGE)
const yearMax = computed(() => datetime().year() + 1)

const rvBrandModel = ref(searchData.value.rvBrand ?? '')

const festivalFriendlyModel = ref(searchData.value.festivalFriendly)
const unlimitedMileageModel = ref(searchData.value.unlimitedMileage)
const experienceModel = ref(searchData.value.experienceNotRequired)
const superhostModel = ref(searchData.value.isSuperhost)

function handleRvTypesChange(rvTypes: RV_TYPES[]) {
  rvTypesModel.value = rvTypes
}

function close() {
  emit('close')
}

async function apply() {
  applyMenuFilter()

  await navigateToRvSearch({
    Types: rvTypesModel.value,
    Delivery: deliveryModel.value,
    InstantBook: instabookModel.value,
    AirportPickup: airportPickupModel.value,
    Amenities: amenitiesModel.value,
    PetFriendly: petFriendlyModel.value,
    MinPrice: priceModel.value.min,
    MaxPrice: priceModel.value.max,
    MinWeight: weightModel.value.min,
    MaxWeight: weightModel.value.max,
    MinLength: lengthModel.value.min,
    MaxLength: lengthModel.value.max,
    MinRvYear: yearModel.value.min,
    MaxRvYear: yearModel.value.max,
    RVBrand: rvBrandModel.value,
    FestivalFriendly: festivalFriendlyModel.value,
    UnlimitedMileage: unlimitedMileageModel.value,
    ExperienceNotRequired: experienceModel.value,
    IsSuperhost: superhostModel.value,
  })

  close()
}

// Expose functions for parent components
defineExpose({
  apply,
  clear,
})

async function clear() {
  applyMenuFilter()

  await clearRvSearchFilters()

  close()
}
</script>

<i18n src="~~/i18n/locales/common/search/filters/rv-types.json" lang="json" />

<i18n src="~~/i18n/locales/common/search/filters/index.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "rvYear": "RV Year",
    "amenities": "Amenities",
    "rules": "Rules",
    "rvManufacturer": "RV Manufacturer",
    "festivalEventsFriendly": "Festival and events friendly",
    "unlimitedMileage": "Unlimited mileage",
    "experienceNotRequired": "Experience not required",
    "moneyDisplay": "${amount}"
  },
  "fr": {
    "rvYear": "Année du VR",
    "amenities": "Équipements",
    "rules": "Règlement",
    "rvManufacturer": "Fabricant de VR",
    "festivalEventsFriendly": "Festivals et événements acceptés",
    "unlimitedMileage": "Kilométrage illimité",
    "experienceNotRequired": "Expérience non requise",
    "moneyDisplay": "{amount} $"
  }
}
</i18n>
